import { useEffect, useCallback, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, MapPin, Hand } from 'lucide-react';
import { useTourStore } from '../../stores/tourStore';
import type { PointerDirection } from '../../stores/tourStore';

/** Measured position of the target element for a tour step */
interface TargetRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

interface ProductTourProps {
  onExpandControls: () => void;
  onCollapseControls: () => void;
  onExpandLeftMenu: () => void;
  onCollapseLeftMenu: () => void;
  onShowBottomSheet: () => void;
  onShowBottomSheetHalf: () => void;
  onHideBottomSheet: () => void;
  onExpandCampaignDock?: () => void;
  onCollapseCampaignDock?: () => void;
}

/*
 * Speech-bubble notch — a pointed edge on the card that faces the feature.
 * Uses an SVG triangle with fill color matching the card surface it exits from:
 *   - up:    exits through the gradient header → teal fill
 *   - down:  exits through the white body     → white fill
 *   - left/right: placed below the header     → white fill
 */
function Notch({ direction, style }: { direction: PointerDirection; style?: React.CSSProperties }) {
  if (direction === 'none') return null;

  const size = 14;  // triangle height
  const width = 24; // triangle base

  // up exits through the gradient header; everything else is white body
  const fill = direction === 'up' ? '#10b981' : 'white';
  const stroke = direction === 'up' ? '#10b981' : '#e5e7eb';

  const isVertical = direction === 'up' || direction === 'down';
  const svgW = isVertical ? width : size;
  const svgH = isVertical ? size : width;

  const points: Record<string, string> = {
    up: `0,${size} ${width / 2},0 ${width},${size}`,
    down: `0,0 ${width / 2},${size} ${width},0`,
    right: `0,0 ${size},${width / 2} 0,${width}`,
    left: `${size},0 0,${width / 2} ${size},${width}`,
  };

  // Default fallback positions (used only if no style override)
  const defaultStyle: React.CSSProperties = (() => {
    switch (direction) {
      case 'up':
        return { position: 'absolute' as const, top: -(size - 1), left: 24 };
      case 'down':
        return { position: 'absolute' as const, bottom: -(size - 1), left: 24 };
      case 'right':
        return { position: 'absolute' as const, right: -(size - 1), top: '50%' };
      case 'left':
        return { position: 'absolute' as const, left: -(size - 1), top: '50%' };
      default:
        return {};
    }
  })();

  return (
    <div style={style || defaultStyle} className="z-10">
      <svg
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="block"
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.08))' }}
      >
        <polygon points={points[direction]} fill={fill} stroke={stroke} strokeWidth="1" />
      </svg>
    </div>
  );
}

export function ProductTour({
  onExpandControls,
  onCollapseControls,
  onExpandLeftMenu,
  onCollapseLeftMenu,
  onShowBottomSheet,
  onShowBottomSheetHalf,
  onHideBottomSheet,
  onExpandCampaignDock,
  onCollapseCampaignDock,
}: ProductTourProps) {
  const { isActive, tourKind, steps, currentStep, currentStepIndex, advanceStep, skipTour } = useTourStore();

  // Store callbacks in a ref so the effect doesn't re-fire when
  // inline arrow props create new references on every render.
  const cbRef = useRef({
    onExpandControls, onCollapseControls,
    onExpandLeftMenu, onCollapseLeftMenu,
    onShowBottomSheet, onShowBottomSheetHalf, onHideBottomSheet,
    onExpandCampaignDock, onCollapseCampaignDock,
  });
  cbRef.current = {
    onExpandControls, onCollapseControls,
    onExpandLeftMenu, onCollapseLeftMenu,
    onShowBottomSheet, onShowBottomSheetHalf, onHideBottomSheet,
    onExpandCampaignDock, onCollapseCampaignDock,
  };

  // Execute step action when step changes
  useEffect(() => {
    if (!isActive || !currentStep) return;
    const cb = cbRef.current;

    switch (currentStep.action) {
      case 'expand-controls':
        cb.onExpandControls();
        break;
      case 'collapse-controls':
        cb.onCollapseControls();
        break;
      case 'expand-left-menu':
        cb.onExpandLeftMenu();
        break;
      case 'collapse-left-menu':
        cb.onCollapseLeftMenu();
        break;
      case 'show-bottom-sheet':
        cb.onShowBottomSheet();
        break;
      case 'show-bottom-sheet-half':
        cb.onShowBottomSheetHalf();
        break;
      case 'hide-bottom-sheet':
        cb.onHideBottomSheet();
        break;
      case 'expand-campaign-dock':
        cb.onExpandCampaignDock?.();
        break;
      case 'collapse-campaign-dock':
        cb.onCollapseCampaignDock?.();
        break;
    }
  }, [isActive, currentStep]);

  const handleNext = useCallback(() => {
    advanceStep();
  }, [advanceStep]);

  // Measure the target element's position on each step change
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      return;
    }
    // Give DOM time to update (e.g. after expand animation)
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-tour-target="${currentStep.id}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top,
          left: r.left,
          right: r.right,
          bottom: r.bottom,
          centerX: r.left + r.width / 2,
          centerY: r.top + r.height / 2,
          width: r.width,
          height: r.height,
        });
      } else {
        setTargetRect(null);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [isActive, currentStep]);

  if (!isActive || !currentStep) return null;

  const isLast = steps.length > 0 && currentStepIndex === steps.length - 1;
  const isFirst = currentStepIndex === 0;
  const middleStepCount = Math.max(steps.length - 2, 0);
  const middleSteps = steps.length > 2 ? steps.slice(1, -1) : [];
  const tourTitle = tourKind === 'campaign' ? 'Field campaign' : 'Coordal Maps';

  /*
   * Compute card position based on the actual measured target element.
   * Pointer direction tells us which side of the target the card sits on.
   * Falls back to centered layout for steps without a DOM target.
   */
  const CARD_WIDTH = 256; // w-64
  const NOTCH_SIZE = 14;
  const GAP = 8; // space between card edge and target element

  let cardStyle: React.CSSProperties = {};
  let notchStyle: React.CSSProperties | undefined = undefined;

  const CARD_HEIGHT_EST = 210; // approximate card height for clamping
  const VIEWPORT_PAD = 16;

  if (targetRect && currentStep.pointer !== 'none') {
    const dir = currentStep.pointer;
    if (dir === 'right') {
      // Card sits to the LEFT of the target, vertically centered on target
      // Clamp so card stays within viewport
      const idealTop = targetRect.centerY - CARD_HEIGHT_EST / 2;
      const clampedTop = Math.max(VIEWPORT_PAD, Math.min(idealTop, window.innerHeight - CARD_HEIGHT_EST - VIEWPORT_PAD));
      cardStyle = {
        position: 'fixed',
        top: clampedTop,
        right: window.innerWidth - targetRect.left + GAP,
        width: CARD_WIDTH,
      };
      // Notch points right, at exact target center Y relative to card top
      const notchTop = targetRect.centerY - clampedTop - 12;
      notchStyle = { position: 'absolute', right: -(NOTCH_SIZE - 1), top: Math.max(20, Math.min(notchTop, CARD_HEIGHT_EST - 40)) };
    } else if (dir === 'left') {
      // Card sits to the RIGHT of the target
      const idealTop = targetRect.centerY - CARD_HEIGHT_EST / 2;
      const clampedTop = Math.max(VIEWPORT_PAD, Math.min(idealTop, window.innerHeight - CARD_HEIGHT_EST - VIEWPORT_PAD));
      cardStyle = {
        position: 'fixed',
        top: clampedTop,
        left: targetRect.right + GAP,
        width: CARD_WIDTH,
      };
      const notchTop = targetRect.centerY - clampedTop - 12;
      notchStyle = { position: 'absolute', left: -(NOTCH_SIZE - 1), top: Math.max(20, Math.min(notchTop, CARD_HEIGHT_EST - 40)) };
    } else if (dir === 'up') {
      // Card sits BELOW the target
      cardStyle = {
        position: 'fixed',
        top: targetRect.bottom + GAP,
        left: targetRect.left,
        right: 12,
        maxWidth: 320,
      };
      notchStyle = { position: 'absolute', top: -(NOTCH_SIZE - 1), left: 24 };
    } else if (dir === 'down') {
      // Card sits ABOVE the target
      cardStyle = {
        position: 'fixed',
        bottom: window.innerHeight - targetRect.top + GAP,
        left: 16,
        right: 16,
        maxWidth: 320,
      };
      notchStyle = { position: 'absolute', bottom: -(NOTCH_SIZE - 1), left: 24 };
    }
  } else {
    // Center fallback (welcome / done / no target found)
    cardStyle = {
      position: 'fixed',
      top: '33%',
      left: 16,
      right: 16,
      maxWidth: 320,
      margin: '0 auto',
    };
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentStep.id}
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.96 }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed z-[100]"
        style={cardStyle}
      >
        {/* Wrapper with overflow-visible so the arrow tip is not clipped */}
        <div className="relative" style={{ overflow: 'visible' }}>
          <Notch direction={currentStep.pointer} style={notchStyle} />

          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl dark:shadow-black/40 border border-gray-200/80 dark:border-gray-700/60">
            {/* Gradient header */}
            <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 px-4 py-2.5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <MapPin size={16} className="text-white" />
                <span className="text-white font-semibold text-sm">
                  {isFirst ? tourTitle : `Step ${currentStepIndex} of ${middleStepCount}`}
                </span>
              </div>
              <button
                type="button"
                onClick={skipTour}
                className="p-0.5 text-white/70 hover:text-white transition-colors"
                aria-label="Skip tour"
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-1.5">
                {currentStep.id === 'welcome' && (
                  <Hand
                    className="w-4 h-4 text-emerald-500 shrink-0 coordal-hand-wave"
                    strokeWidth={2}
                    aria-hidden
                  />
                )}
                {currentStep.title}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                {currentStep.description}
              </p>

              {/* Actions */}
              <div className="flex gap-2">
                {!isFirst && !isLast && (
                  <button
                    type="button"
                    onClick={skipTour}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                  >
                    Skip
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 py-2.5 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity shadow-sm"
                >
                  {isFirst
                    ? "Let's go"
                    : isLast
                      ? tourKind === 'campaign'
                        ? 'Got it'
                        : 'Start exploring'
                      : 'Next'}
                  {!isLast && <ArrowRight size={14} />}
                </button>
              </div>

              {/* Step dots */}
              {!isFirst && !isLast && (
                <div className="flex justify-center gap-1.5 mt-3">
                  {middleSteps.map((step, i) => (
                    <div
                      key={step.id}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentStepIndex - 1
                        ? 'bg-emerald-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
